import React, { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import Select from "react-select";
import clsx from "clsx";
import {
	Button,
	Callout,
	Card,
	FormGroup,
	Icon,
	InputGroup,
	Intent,
	NonIdealState,
	Spinner,
	Tag,
} from "@blueprintjs/core";

import { IStore } from "redux/store";
import toolSelectors from "redux/app/tool/selectors";
import toolActions from "redux/app/tool/actions";
import { getTenantsOptionList } from "redux/app/tool/utils";
import { commonUtils } from "redux/_common";
import authConstants from "redux/app/auth/constants";
import {
	clearIndicatorStyles,
	controlStyles,
	dropdownIndicatorStyles,
	menuStyles,
	optionStyles,
	placeholderStyles,
} from "redux/app/feedback/utils";
import { IOption } from "redux/app/feedback/@types";

type OwnProps = {};
type PortsCargoListProps = PropsFromRedux & OwnProps;

type PortsCargoResponse = {
	tenantId: string;
	ports: string[];
	cargoTypes: Array<{ label: string; value: string }>;
	updatedAt?: { ports: string | null; cargo: string | null };
	missing?: { portsList: boolean; cargoList: boolean };
};

function PortsCargoList(props: PortsCargoListProps) {
	const [selectedTenant, setSelectedTenant] = useState<IOption | undefined>();
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [data, setData] = useState<PortsCargoResponse | null>(null);

	const formatLocalDateTimeAMPM = (iso?: string | null) => {
		if (!iso) return null;
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return null;
		return d.toLocaleString(undefined, {
			year: "numeric",
			month: "short",
			day: "2-digit",
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};

	const formatUtcTimestamp = (iso?: string | null) => {
		if (!iso) return null;
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return null;
		return d.toISOString(); // always UTC
	};

	useEffect(() => {
		props.loadTenantsList();
	}, []);

	const fetchPortsCargo = async (tenantId: string) => {
		setLoading(true);
		setErrorMessage(null);
		setData(null);

		try {
			const resp = await fetch(
				`${commonUtils.getAPIBaseUrl()}/api/ports-cargo-list?tenantId=${encodeURIComponent(
					tenantId
				)}`,
				{
					headers: {
						[authConstants.headers.TOKEN]: props.authToken || "",
					},
				}
			);

			const json = (await resp.json()) as PortsCargoResponse | { message?: string };
			if (!resp.ok) {
				setErrorMessage(
					(json as any)?.message || "Failed to load ports/cargo list"
				);
				return;
			}

			setData(json as PortsCargoResponse);
		} catch (e: any) {
			setErrorMessage(e?.message || "Network error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!selectedTenant?.value) return;
		fetchPortsCargo(selectedTenant.value);
	}, [selectedTenant?.value]);

	const normalizedSearch = search.trim().toLowerCase();

	const filteredPorts = useMemo(() => {
		const ports = data?.ports ?? [];
		if (!normalizedSearch) return ports;
		return ports.filter((p) => p.toLowerCase().includes(normalizedSearch));
	}, [data?.ports, normalizedSearch]);

	const filteredCargoTypes = useMemo(() => {
		const cargos = data?.cargoTypes ?? [];
		if (!normalizedSearch) return cargos;
		return cargos.filter((c) => {
			const label = (c?.label || "").toLowerCase();
			const value = (c?.value || "").toLowerCase();
			return label.includes(normalizedSearch) || value.includes(normalizedSearch);
		});
	}, [data?.cargoTypes, normalizedSearch]);

	const hasTenant = !!selectedTenant?.value;
	const hasData = !!data && !loading && !errorMessage;

	return (
		<div className="min-h-[calc(100vh-98px)] bg-gray-50 p-6 md:p-8 lg:p-12">
			<div className="max-w-[1600px] mx-auto space-y-6">
				<div className="flex items-center justify-between gap-4 flex-wrap">
					<div>
						<h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
							<Icon icon="map" size={28} className="text-blue-600" />
							Ports & Cargo List
						</h1>
						<p className="text-gray-600">
							View ports and cargo types for a selected tenant.
						</p>
					</div>

					<Button
						icon="refresh"
						intent={Intent.PRIMARY}
						disabled={!hasTenant || loading}
						loading={loading}
						onClick={() => selectedTenant?.value && fetchPortsCargo(selectedTenant.value)}
					>
						Refresh
					</Button>
				</div>

				{errorMessage && (
					<Callout intent={Intent.DANGER} icon="error" title="Error">
						{errorMessage}
					</Callout>
				)}

				<Card elevation={1} className="bg-white">
					<div className="p-2">
						<h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
							<Icon icon="filter" className="text-gray-500" />
							Selection & Search
						</h2>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<FormGroup label="Select Tenant" labelInfo="(required)" className="mb-0">
								<Select
									id="tenants"
									isClearable
									isSearchable
									placeholder={"Search tenants"}
									isDisabled={loading}
									value={selectedTenant}
									options={getTenantsOptionList(props.tenants)}
									isLoading={props.jobToLoadTenants.isLoading}
									onChange={(newValue) => setSelectedTenant(newValue ?? undefined)}
									className="w-full text-sm"
									menuPosition="fixed"
									classNames={{
										option: ({ isFocused, isSelected }) =>
											clsx(
												isFocused && optionStyles.focus,
												isSelected && optionStyles.selected,
												optionStyles.base
											),
										dropdownIndicator: () => dropdownIndicatorStyles,
										menu: () => menuStyles,
										clearIndicator: () => clearIndicatorStyles,
										placeholder: () => placeholderStyles,
										control: ({ isFocused, isDisabled }) =>
											clsx(
												isFocused ? controlStyles.focus : controlStyles.nonFocus,
												isDisabled ? controlStyles.disabled : controlStyles.base
											),
										valueContainer: ({ isDisabled }) =>
											clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
									}}
								/>
							</FormGroup>

							<FormGroup
								label="Search"
								labelInfo="(filters both lists)"
								className="mb-0"
							>
								<InputGroup
									leftIcon="search"
									placeholder="Search ports or cargo types..."
									value={search}
									disabled={!hasTenant || loading}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setSearch(e.target.value)
									}
								/>
							</FormGroup>
						</div>

						{hasData && (
							<div className="mt-4 flex flex-wrap gap-2 text-xs text-gray-600">
								<Tag minimal>
									Ports: {filteredPorts.length}/{data?.ports?.length ?? 0}
								</Tag>
								<Tag minimal>
									Cargo: {filteredCargoTypes.length}/{data?.cargoTypes?.length ?? 0}
								</Tag>
								{data?.updatedAt?.ports && (
									<Tag
										minimal
										title={
											formatUtcTimestamp(data.updatedAt.ports) ??
											data.updatedAt.ports
										}
									>
										Ports updated:{" "}
										{formatLocalDateTimeAMPM(data.updatedAt.ports) ??
											data.updatedAt.ports}
									</Tag>
								)}
								{data?.updatedAt?.cargo && (
									<Tag
										minimal
										title={
											formatUtcTimestamp(data.updatedAt.cargo) ??
											data.updatedAt.cargo
										}
									>
										Cargo updated:{" "}
										{formatLocalDateTimeAMPM(data.updatedAt.cargo) ??
											data.updatedAt.cargo}
									</Tag>
								)}
								{data?.missing?.portsList && (
									<Tag intent={Intent.WARNING}>Ports list missing</Tag>
								)}
								{data?.missing?.cargoList && (
									<Tag intent={Intent.WARNING}>Cargo list missing</Tag>
								)}
							</div>
						)}
					</div>
				</Card>

				{!hasTenant ? (
					<NonIdealState
						icon="search"
						title="Select a tenant"
						description="Choose a tenant to load its ports and cargo list."
						className="py-12 bg-white rounded-lg shadow-sm border border-gray-100"
					/>
				) : loading ? (
					<div className="py-12 flex justify-center">
						<Spinner size={40} />
					</div>
				) : errorMessage ? null : data ? (
					<div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
						<Card elevation={1} className="bg-white">
							<div className="p-2">
								<h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
									<Icon icon="map-marker" className="text-blue-600" />
									Ports
									<span className="text-sm text-gray-500 font-normal">
										({filteredPorts.length})
									</span>
								</h2>

								{filteredPorts.length === 0 ? (
									<NonIdealState
										icon="filter"
										title="No matching ports"
										description={
											normalizedSearch
												? "Try a different search term."
												: "No ports found for this tenant."
										}
									/>
								) : (
									<div className="max-h-[65vh] overflow-auto border border-gray-100 rounded-lg">
										<ul className="divide-y divide-gray-100">
											{filteredPorts.map((p, idx) => (
												<li
													key={`${p}-${idx}`}
													className="px-4 py-3 text-sm text-gray-800"
												>
													{p}
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						</Card>

						<Card elevation={1} className="bg-white">
							<div className="p-2">
								<h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
									<Icon icon="cube" className="text-blue-600" />
									Cargo Types
									<span className="text-sm text-gray-500 font-normal">
										({filteredCargoTypes.length})
									</span>
								</h2>

								{filteredCargoTypes.length === 0 ? (
									<NonIdealState
										icon="filter"
										title="No matching cargo types"
										description={
											normalizedSearch
												? "Try a different search term."
												: "No cargo types found for this tenant."
										}
									/>
								) : (
									<div className="max-h-[65vh] overflow-auto border border-gray-100 rounded-lg">
										<ul className="divide-y divide-gray-100">
											{filteredCargoTypes.map((c) => (
												<li
													key={`${c.value}-${c.label}`}
													className="px-4 py-3 text-sm text-gray-800 flex items-center justify-between gap-3"
												>
													<span className="truncate">{c.label}</span>
													<span className="text-xs text-gray-500 font-mono shrink-0">
														{c.value}
													</span>
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						</Card>
					</div>
				) : (
					<NonIdealState
						icon="database"
						title="No data loaded"
						description="Pick a tenant and click Refresh."
						className="py-12 bg-white rounded-lg shadow-sm border border-gray-100"
					/>
				)}
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const toolStore = toolSelectors._getStore(store);
	return {
		tenants: toolSelectors.getTenants(toolStore),
		jobToLoadTenants: toolSelectors.jobToLoadTenants(toolStore),
		authToken: store.app.auth.authToken,
	};
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		loadTenantsList() {
			dispatch(toolActions.commands.toolTenantListLoad());
		},
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(PortsCargoList);


